/**
 * Modal Stack Context - Shared z-index management for modal components
 *
 * Provides a unified z-index hierarchy for all modal-like components
 * (Modal, Dialog, AlertDialog, Sheet). Each component reads its depth
 * from context and calculates z-index dynamically, preventing stacking bugs.
 *
 * Z-Index Hierarchy (single source of truth):
 *
 * 1037: Sidebar (#sidebar in sidebar.less)
 * 1039: Status banner (.schedugram-status in dashboard.less)
 * 1040: Menu overlay (menu.tsx)
 * 1050: Sheet overlay + content (base)
 * 1055: Modal backdrop (base, auto-increments by Z_INDEX_STEP per depth)
 * 1056: Modal container (base)
 * 1060: Modal content / Dialog overlay+content / AlertDialog overlay+content (base)
 * +10 per nesting depth (1070, 1080, etc.)
 * 99999: Toast (always on top)
 */
import * as React from "react";

// Base z-index values - each nested layer adds Z_INDEX_STEP to these
export const Z_INDEX_SHEET = 1050;
export const Z_INDEX_MODAL_BACKDROP = 1055;
export const Z_INDEX_MODAL_CONTAINER = 1056;
export const Z_INDEX_MODAL_CONTENT = 1060;
export const Z_INDEX_STEP = 10;

export interface ModalStackContextValue {
  depth: number;
  register: () => number;
  unregister: (depth: number) => void;
}

export const ModalStackContext =
  React.createContext<ModalStackContextValue | null>(null);

/**
 * Hook to get the current modal depth from context.
 * Returns 0 if no context exists (first modal in the tree).
 */
export function useModalStack() {
  const context = React.useContext(ModalStackContext);
  const [depth, setDepth] = React.useState(0);

  React.useEffect(() => {
    if (context) {
      const registeredDepth = context.register();
      setDepth(registeredDepth);
      return () => context.unregister(registeredDepth);
    }
    return undefined;
  }, [context]);

  return context ? depth : 0;
}

/**
 * Selector for Radix portaled content that renders outside the dialog DOM tree.
 * Used by Dialog, AlertDialog, and Modal to prevent closing when the user
 * clicks on a portaled child (popover, dropdown, select, etc.).
 *
 * Update this list when adding new portaled Radix components:
 *   [data-radix-popper-content-wrapper] — all Radix Popper-based portals (Select, Combobox, Tooltip, etc.)
 *   [data-radix-menu-content]           — Radix DropdownMenu / ContextMenu
 *   [data-slot="dialog-content"]        — Radix Dialog / AlertDialog content
 *   [data-slot="dialog-overlay"]        — Radix Dialog / AlertDialog overlay
 *   [data-slot="dropdown-menu-content"] — our wrapped DropdownMenu component
 *   [data-slot="popover-content"]       — our wrapped Popover component
 *   [data-slot="select-content"]        — our wrapped Select component
 *   [data-slot="modal-content"]         — custom Modal content (non-shadcn)
 *   [data-slot="modal-backdrop"]        — custom Modal backdrop
 *   [data-slot="modal-container"]       — custom Modal scroll container
 */
const RADIX_PORTAL_SELECTOR =
  '[data-radix-popper-content-wrapper], [data-radix-menu-content], [data-slot="dialog-content"], [data-slot="dialog-overlay"], [data-slot="dropdown-menu-content"], [data-slot="popover-content"], [data-slot="select-content"], [data-slot="modal-content"], [data-slot="modal-backdrop"], [data-slot="modal-container"], [data-slot="sheet-content"], [data-slot="sheet-overlay"]';

/**
 * Returns true if the event target is inside a Radix portaled element.
 * Call from onInteractOutside / onPointerDownOutside / onFocusOutside
 * handlers to prevent the parent overlay from closing.
 */
export function isInsideRadixPortal(e: Event): boolean {
  const target = e.target;
  return target instanceof Element
    ? !!target.closest(RADIX_PORTAL_SELECTOR)
    : false;
}

/**
 * Provider that tracks modal stack depth.
 * Each modal-like component renders this provider for its children,
 * incrementing the depth so nested components stack correctly.
 */
export function ModalStackProvider({
  children,
  parentDepth,
}: {
  children: React.ReactNode;
  parentDepth: number;
}) {
  // usedDepthsRef persists across useMemo re-runs when parentDepth changes.
  // Depths registered under the old parentDepth remain in the Set; the new
  // register closure skips over them. parentDepth changing on a mounted provider
  // is not expected in practice.
  const usedDepthsRef = React.useRef(new Set<number>());

  const value = React.useMemo<ModalStackContextValue>(
    () => ({
      depth: parentDepth,
      register: () => {
        let depth = parentDepth + 1;
        while (usedDepthsRef.current.has(depth)) depth++;
        usedDepthsRef.current.add(depth);
        return depth;
      },
      unregister: (depth: number) => {
        usedDepthsRef.current.delete(depth);
      },
    }),
    [parentDepth]
  );

  return (
    <ModalStackContext.Provider value={value}>
      {children}
    </ModalStackContext.Provider>
  );
}
